// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DataTableContext from "@eisinc/MobiusOverrides/DataTable/DataTableContext";
import { color } from "@eisinc/XDL/Utilities";
import printStyles, { onPrint } from "@eisinc/XDL/Utilities/print";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";

export type DataTableRowProps = MobiusStyledComponentProps<
    "tr",
    {
        /** CSS string or styled-components function to be injected into this component. */
        css?: StyledProp<DataTableRowProps>;
        evenRowCss?: StyledProp<DataTableRowProps>;
        /**
         * Indicates how the `css` property is combined with the default `css` property from the theme.
         * If true, the default css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the default css in the theme.
         */
        mergeCss?: boolean;
    }
>;

// XNG-Change
const printStyle = css`
    ${printStyles.avoidBreakInside}
    ${onPrint(css`
        border-bottom: 1px solid ${color("common.ui1")};
    `)}
`;

const DataTableRowStyle = styled.tr<
    DataTableRowProps & {
        evenRowCss?: StyledProp<DataTableRowProps>;
    }
>`
    background: ${getColor("common.background")};
    &:nth-child(even) {
        background: ${getColor("common.accent")};
        ${({ evenRowCss }) => evenRowCss}
    }
    ${printStyle}
    ${injectCss}
`;

// XNG-Changes
const MobileDataTableRowStyle = styled.div<
    DataTableRowProps & {
        evenRowCss?: StyledProp<DataTableRowProps>;
    }
>`
    display: flex;
    flex-direction: column;
    background: ${getColor("common.background")};
    padding: 8px;
    row-gap: 12px;

    &:nth-child(even) {
        background: ${getColor("common.accent")};
        ${({ evenRowCss }) => evenRowCss}
    }
    ${printStyle}
    ${injectCss}
`;

const DataTableRow: React.FC<DataTableRowProps> = ({ mergeCss, css: dataTableCss = "", ...otherProps }) => {
    const { applyStyledProp } = applyPropBuilder({ ...otherProps }, { component: "dataTable" });
    const { theme } = otherProps;
    const resolvedMergeCss = mergeCss ?? theme?.dataTable.defaultProps?.mergeCss;

    // XNG-Changes: add MobileDataTableRowStyle
    return (
        <DataTableContext.Consumer>
            {({ isTabletScreen, useResponsiveViewForTablet }) =>
                isTabletScreen && useResponsiveViewForTablet ? (
                    <MobileDataTableRowStyle
                        css={css`
                            ${applyStyledProp(["cssOverrides", "row"], resolvedMergeCss)}
                            ${dataTableCss}
                        `}
                        evenRowCss={applyStyledProp(["cssOverrides", "evenRow"], resolvedMergeCss)}
                        {...otherProps}
                    />
                ) : (
                    <DataTableRowStyle
                        css={css`
                            ${applyStyledProp(["cssOverrides", "row"], resolvedMergeCss)}
                            ${dataTableCss}
                        `}
                        evenRowCss={applyStyledProp(["cssOverrides", "evenRow"], resolvedMergeCss)}
                        {...otherProps}
                    />
                )
            }
        </DataTableContext.Consumer>
    );
};

export default withTheme(DataTableRow);
